import { useState } from 'react'
import { useQuery } from '@apollo/client'
import { Link, useNavigate } from 'react-router-dom'
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  TagIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyEuroIcon
} from '@heroicons/react/24/outline'
import { GET_OPTION_ITEMS, GET_OPTION_GROUPS } from '../graphql/product-queries'
// import { DELETE_OPTION_ITEM } from '../graphql/mutations'
import { OptionItem, OptionItemFilters, OptionGroup } from '../types/product'
import LoadingSpinner from '../components/LoadingSpinner'
// import toast from 'react-hot-toast'

const OptionItemsPage = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(undefined)
  const [defaultFilter, setDefaultFilter] = useState<boolean | undefined>(undefined)

  const filters: OptionItemFilters = {
    search: searchTerm || undefined,
    option_group_id: selectedGroupId,
    default_selected: defaultFilter
  }

  const { data: optionItemsData, loading: itemsLoading, error: itemsError, refetch } = useQuery(GET_OPTION_ITEMS, {
    variables: { filters }
  })

  const { data: optionGroupsData, loading: groupsLoading } = useQuery(GET_OPTION_GROUPS)

  // const [deleteOptionItem] = useMutation(DELETE_OPTION_ITEM, {
  //   onCompleted: () => {
  //     toast.success('Option item deleted successfully')
  //     refetch()
  //   },
  //   onError: (error) => {
  //     toast.error(`Failed to delete option item: ${error.message}`)
  //   }
  // })

  const optionItems = optionItemsData?.optionItems || []
  const optionGroups = optionGroupsData?.optionGroups || []

  const handleSearch = (value: string) => {
    setSearchTerm(value)
  }

  const handleGroupFilterChange = (value: number | undefined) => {
    setSelectedGroupId(value)
  }

  const handleDefaultFilterChange = (value: boolean | undefined) => {
    setDefaultFilter(value)
  }

  // const handleDelete = async (id: number, label: string) => {
  //   if (window.confirm(`Are you sure you want to delete "${label}"? This action cannot be undone.`)) {
  //     try {
  //       await deleteOptionItem({
  //         variables: { id }
  //       })
  //     } catch (error) {
  //       // Error is handled in the mutation's onError callback
  //     }
  //   }
  // }

  const handleRowClick = (optionItemId: number) => {
    navigate(`/option-items/${optionItemId}`)
  }

  const formatPrice = (cents: number) => {
    if (cents === 0) return 'No charge'
    const sign = cents > 0 ? '+' : ''
    return `${sign}€${(cents / 100).toFixed(2)}`
  }

  const getGroupName = (groupId: number) => {
    const group = optionGroups.find((g: OptionGroup) => g.id === groupId)
    return group?.name || `Group ${groupId}`
  }

  if (itemsLoading || groupsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (itemsError) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">⚠️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading option items</h3>
        <p className="text-gray-500 mb-6">{itemsError.message}</p>
        <button 
          onClick={() => refetch()}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Option Items</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage individual options within option groups
          </p>
        </div>
        <Link
          to="/option-items/new"
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Option Item
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search option items..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Option Group Filter */}
          <div className="min-w-0 flex-1 lg:max-w-xs">
            <select
              value={selectedGroupId || ''}
              onChange={(e) => handleGroupFilterChange(e.target.value ? Number(e.target.value) : undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Groups</option>
              {optionGroups.map((group: OptionGroup) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          {/* Default Filter */}
          <div className="flex border border-gray-300 rounded-lg">
            <button
              onClick={() => handleDefaultFilterChange(undefined)}
              className={`px-3 py-2 ${defaultFilter === undefined ? 'bg-primary-500 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              All
            </button>
            <button
              onClick={() => handleDefaultFilterChange(true)}
              className={`px-3 py-2 ${defaultFilter === true ? 'bg-primary-500 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              Default
            </button>
            <button
              onClick={() => handleDefaultFilterChange(false)}
              className={`px-3 py-2 ${defaultFilter === false ? 'bg-primary-500 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              Optional
            </button>
          </div>
        </div>
      </div>

      {/* Option Items Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Label
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Option Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price Delta
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Default
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sort Order
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {optionItems.map((optionItem: OptionItem) => (
                <tr 
                  key={optionItem.id} 
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleRowClick(optionItem.id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <TagIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{optionItem.label}</div>
                        <div className="text-sm text-gray-500">ID: {optionItem.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getGroupName(optionItem.option_group_id)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <CurrencyEuroIcon className="h-4 w-4 text-gray-400 mr-1" />
                      <span className={`font-medium ${optionItem.price_delta_cents > 0 ? 'text-green-600' : optionItem.price_delta_cents < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                        {formatPrice(optionItem.price_delta_cents)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {optionItem.default_selected ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Default
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <XCircleIcon className="h-3 w-3 mr-1" />
                        Optional
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {optionItem.sort_order || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {optionItems.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🏷️</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No option items found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || selectedGroupId || defaultFilter !== undefined
              ? 'Try adjusting your search or filters'
              : 'Get started by creating your first option item'
            }
          </p>
          {!searchTerm && !selectedGroupId && defaultFilter === undefined && (
            <Link to="/option-items/new" className="btn-primary">
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Option Item
            </Link>
          )}
        </div>
      )}
    </div>
  )
}

export default OptionItemsPage
